// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { listMessages, markRead, sendMessage, subscribeToRoomMessages, listReceiptsForMessages, listRoomParticipants, getProfilesByIds } from '@/services/chat';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, ShieldAlert, Check, CheckCheck, Loader2, XCircle, UserCircle2, FileText, ScanLine, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { convertHeicToJpeg, validateImageFile, compressImage } from '@/utils/image';
import { submitReport } from '@/services/reports';
import { playSound } from '@/utils/sound';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useAuth } from '@/providers/AuthProvider';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { adminDeleteAllMessagesInRoom, adminDeleteMessage } from '@/services/chat';
import ImageLightbox from '@/components/chat/ImageLightbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePresence } from '@/hooks/usePresence';

interface ChatWindowProps {
  roomId: string;
  title?: string;
  onReport?: (messageId: string) => void;
  onOpenProfile?: (userId: string) => void;
}

export default function ChatWindow({ roomId, title, onReport, onOpenProfile }: ChatWindowProps) {
  const { user, isAdmin } = useAuth();
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<{ name: string; status: 'uploading' | 'error' | 'done' }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
  const { onlineUsers } = usePresence(roomId);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newNotice, setNewNotice] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const lastEventRef = useRef<number>(Date.now());
  const pollIntervalRef = useRef<number | null>(null);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  const refresh = async () => {
    try {
      const data = await listMessages(roomId, 200);
      // Attach signed URLs for private storage objects
      const withUrls = await Promise.all(
        (data || []).map(async (m) => {
          if (m.attachment_url) {
            try {
              const { data: signed } = await supabase.storage
                .from('message-attachments')
                .createSignedUrl(m.attachment_url, 3600);
              return { ...m, signed_url: signed?.signedUrl || null };
            } catch {
              return { ...m, signed_url: null };
            }
          }
          return m;
        })
      );
      // Load receipts for read ticks
      let receipts: any[] = [];
      try {
        receipts = await listReceiptsForMessages((data || []).map((m) => m.id));
      } catch {}
      const receiptsMap: Record<string, number> = {};
      for (const r of receipts) receiptsMap[r.message_id] = (receiptsMap[r.message_id] || 0) + 1;
      setMessages(withUrls.map((m) => ({ ...m, __receiptsCount: receiptsMap[m.id] || 0 })));
      await markRead((data || []).map(m => m.id));
      // Persist last viewed time for unread divider
      try {
        localStorage.setItem(`chat:lastViewed:${roomId}`, new Date().toISOString());
      } catch {}
    } catch (e: any) {
      console.error(e);
    }
  };

  useEffect(() => {
    const loadParticipants = async () => {
      try {
        const ids = await listRoomParticipants(roomId);
        const unique = Array.from(new Set(ids));
        const profiles = await getProfilesByIds(unique);
        setParticipants(profiles);
      } catch {}
    };
    loadParticipants();
  }, [roomId]);

  useEffect(() => {
    const firstLoad = async () => {
      await refresh();
    };
    firstLoad();

    // Realtime subscription with lightweight debounce
    let debounceTimer: number | null = null;
    const unsub = subscribeToRoomMessages(roomId, async () => {
      lastEventRef.current = Date.now();
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(async () => {
        await refresh();
        playSound('incoming');
      }, 150);
    });

    // Fallback polling every 2s if no realtime events are received
    if (pollIntervalRef.current) window.clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = window.setInterval(async () => {
      const since = Date.now() - lastEventRef.current;
      // If we haven't received an event in > 2500ms and tab is visible, poll
      if (since > 2500 && !document.hidden) {
        await refresh();
      }
    }, 2000);

    // Visibility-driven refresh
    const onVisibility = async () => {
      if (!document.hidden) {
        await refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsub();
      if (pollIntervalRef.current) window.clearInterval(pollIntervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [roomId]);

  useEffect(() => {
    if (atBottom) {
      scrollToBottom();
      setNewNotice(false);
    } else {
      setNewNotice(true);
    }
  }, [messages.length]);

  // Load saved draft for this room
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`chat:draft:${roomId}`);
      if (saved) setText(saved);
    } catch {}
  }, [roomId]);

  // Flush offline queue when back online
  useEffect(() => {
    const flush = async () => {
      const qKey = 'chat:offlineQueue';
      let queue: any[] = [];
      try { queue = JSON.parse(localStorage.getItem(qKey) || '[]'); } catch {}
      if (!queue.length) return;
      const remaining: any[] = [];
      for (const item of queue) {
        try {
          if (item.roomId && item.content) {
            // eslint-disable-next-line no-await-in-loop
            await sendMessage(item.roomId, item.content, null);
          } else remaining.push(item);
        } catch {
          remaining.push(item);
        }
      }
      try { localStorage.setItem(qKey, JSON.stringify(remaining)); } catch {}
      await refresh();
    };
    const handler = () => flush();
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [roomId]);

  // Removed file picker icon; drag-and-drop and paste only

  // Support drag & drop and paste
  const onDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) await handleFiles(files);
  };
  const onPaste: React.ClipboardEventHandler<HTMLDivElement> = async (e) => {
    const items = Array.from(e.clipboardData.items || []);
    for (const it of items) {
      const file = it.getAsFile();
      if (file) {
        await handleFiles([file]);
        break;
      }
    }
  };

  const onViewportScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setAtBottom(nearBottom);
    if (nearBottom) setNewNotice(false);
  };

  const handleFile = async (file: File) => {
    // Detect type robustly
    const nameLower = (file.name || '').toLowerCase();
    const typeLower = (file.type || '').toLowerCase();
    const isHeicFile = /\.heic$/.test(nameLower) || typeLower === 'image/heic' || typeLower === 'image/heif';
    const isPdf = typeLower === 'application/pdf' || /\.pdf$/.test(nameLower);
    const isGif = typeLower === 'image/gif' || /\.gif$/.test(nameLower);
    const isImage = typeLower.startsWith('image/') || isHeicFile;

    if (!isImage && !isPdf) {
      toast({ title: 'Invalid file', description: 'Only images or PDF up to 100MB are allowed', variant: 'destructive' });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast({ title: 'Too large', description: 'Max file size is 100MB', variant: 'destructive' });
      return;
    }

    let toUpload = file;
    try {
      if (isHeicFile) {
        toUpload = await convertHeicToJpeg(file);
      }
      // Do not compress PDFs; compress images (skip GIF to preserve animation)
      if (isImage && !isGif) {
        toUpload = await compressImage(toUpload, 8);
      }
    } catch {}

    try {
      setUploading(true);
      setUploadQueue((q) => [...q, { name: toUpload.name, status: 'uploading' }]);
      const safeName = toUpload.name.replace(/[^a-z0-9.\-_]+/gi, '_');
      const path = `room/${roomId}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from('message-attachments').upload(path, toUpload, { upsert: false, contentType: toUpload.type || (isPdf ? 'application/pdf' : isGif ? 'image/gif' : 'image/jpeg') });
      if (upErr) throw upErr;
      const storagePath = path; // we store the storage object key; signed URL created on fetch
      // optimistic placeholder
      setMessages((prev) => [...prev, { id: `temp-${Date.now()}`, room_id: roomId, sender_id: user?.id, attachment_url: storagePath, signed_url: null, attachment_mime: toUpload.type || (isPdf ? 'application/pdf' : isGif ? 'image/gif' : 'image/jpeg'), content: null, created_at: new Date().toISOString(), __optimistic: true }]);
      await sendMessage(roomId, null, { url: storagePath, mime: toUpload.type || (isPdf ? 'application/pdf' : isGif ? 'image/gif' : 'image/jpeg') });
      setUploadQueue((q) => q.map((it) => it.name === toUpload.name ? { ...it, status: 'done' } : it));
      await refresh();
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Upload failed', description: e.message || 'Unable to upload file', variant: 'destructive' });
      setUploadQueue((q) => q.map((it) => it.name === file.name ? { ...it, status: 'error' } : it));
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (files: File[]) => {
    for (const f of files) {
      // sequential to keep UI simple
      // eslint-disable-next-line no-await-in-loop
      await handleFile(f);
    }
  };

  const onSend = async () => {
    if (!text.trim()) return;
    try {
      const payload = text.trim();
      // offline queue
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const qKey = 'chat:offlineQueue';
        const entry = { roomId, content: payload, at: Date.now() };
        try {
          const prev = JSON.parse(localStorage.getItem(qKey) || '[]');
          prev.push(entry);
          localStorage.setItem(qKey, JSON.stringify(prev));
        } catch {}
        setMessages((prev) => [...prev, { id: `queued-${Date.now()}`, room_id: roomId, sender_id: user?.id, content: payload, created_at: new Date().toISOString(), __queued: true }]);
        setText('');
        toast({ title: 'Queued', description: 'Message will send when online.' });
        return;
      }
      // optimistic send
      const tempId = `temp-${Date.now()}`;
      setMessages((prev) => [...prev, { id: tempId, room_id: roomId, sender_id: user?.id, content: payload, created_at: new Date().toISOString(), __optimistic: true }]);
      setText('');
      await sendMessage(roomId, payload, null);
      await refresh();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to send', variant: 'destructive' });
    }
  };

  return (
    <Card className="bg-white/5 border-white/10 h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex -space-x-2">
              {participants.filter((p) => p.id !== user?.id).slice(0, 3).map((p) => (
                <div key={p.id} className="h-12 w-12 shrink-0 rounded-full border border-white/20 overflow-hidden bg-white/10 flex items-center justify-center">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt="avatar" className="h-full w-full object-cover object-center" />
                  ) : (
                    <UserCircle2 className="h-6 w-6 text-white/70" />
                  )}
                </div>
              ))}
              {participants.filter((p) => p.id !== user?.id).length > 3 && (
                <div className="h-12 w-12 shrink-0 rounded-full border border-white/20 bg-white/10 text-white/80 text-[10px] flex items-center justify-center">+{participants.length - 3}</div>
              )}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base text-white truncate tracking-wide">
                {(() => {
                  if (title) return title;
                  const names = participants
                    .filter((p) => p.id !== user?.id)
                    .map((p) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || 'User');
                  if (names.length === 0 && participants.length > 0) {
                    return [participants[0].first_name, participants[0].last_name].filter(Boolean).join(' ') || 'Chat';
                  }
                  return names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '') || 'Chat';
                })()}
              </CardTitle>
              <div className="text-xs text-white/60">
                {(() => {
                  const others = participants.filter((p) => p.id !== user?.id);
                  if (others.length <= 1) {
                    const peer = others[0];
                    const isOnline = peer ? !!onlineUsers[peer.id] : false;
                    return isOnline ? 'Online' : 'Offline';
                  }
                  const total = others.length;
                  const online = others.reduce((acc, p) => acc + (onlineUsers[p.id] ? 1 : 0), 0);
                  return `${online}/${total} online`;
                })()}
              </div>
            </div>
          </div>
          {participants.filter((p) => p.id !== user?.id).length === 1 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20"
                onClick={() => {
                  const peer = participants.find((p) => p.id !== user?.id);
                  if (!peer) return;
                  if (onOpenProfile) onOpenProfile(peer.id);
                }}
              >
                View profile
              </Button>
            </div>
          )}
        </div>
        <div className="mt-2 h-px bg-gradient-to-r from-emerald-500/50 to-teal-500/50" />
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        <ScrollArea className="flex-1 h-0 p-4" onScroll={onViewportScroll} ref={viewportRef as any} onDrop={onDrop} onDragOver={(e) => e.preventDefault()} onPaste={onPaste}>
          <div className="space-y-4">
            {(() => {
              const items: React.ReactNode[] = [];
              let lastDay: string | null = null;
              const myId = user?.id;
              // Unread divider index using last viewed timestamp
              let unreadInserted = false;
              let lastViewed: Date | null = null;
              try {
                const ts = localStorage.getItem(`chat:lastViewed:${roomId}`);
                if (ts) lastViewed = new Date(ts);
              } catch {}

              for (let i = 0; i < messages.length; i++) {
                const m = messages[i];
                const day = new Date(m.created_at).toDateString();
                if (lastDay !== day) {
                  lastDay = day;
                  items.push(
                    <div key={`day-${day}-${i}`} className="sticky top-0 z-0 flex items-center gap-2 my-4 transition-opacity">
                      <div className="flex-1 h-px bg-gradient-to-r from-white/0 via-white/15 to-white/0" />
                      <span className="text-xs text-white/70 px-2 py-0.5 rounded-full bg-white/10 border border-white/20">
                        {new Date(m.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-white/0 via-white/15 to-white/0" />
                    </div>
                  );
                }
                if (!unreadInserted && lastViewed && new Date(m.created_at) > lastViewed) {
                  unreadInserted = true;
                  items.push(
                    <div key={`unread-${i}`} className="flex items-center gap-2 my-2">
                      <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/0 via-emerald-500/30 to-emerald-500/0" />
                      <span className="text-xs text-emerald-300 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 shadow-[0_0_20px_rgba(16,185,129,0.25)]">Unread</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/0 via-emerald-500/30 to-emerald-500/0" />
                    </div>
                  );
                }

                const isMine = myId && m.sender_id === myId;
                const prev = i > 0 ? messages[i - 1] : null;
                const next = i < messages.length - 1 ? messages[i + 1] : null;
                const within = (a: any, b: any) => Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 5 * 60 * 1000;
                const prevSame = prev && prev.sender_id === m.sender_id && within(prev.created_at, m.created_at);
                const nextSame = next && next.sender_id === m.sender_id && within(next.created_at, m.created_at);
                const radiusAdjust = [
                  prevSame ? (isMine ? 'rounded-tr-md' : 'rounded-tl-md') : '',
                  nextSame ? (isMine ? 'rounded-br-md' : 'rounded-bl-md') : ''
                ].join(' ');
                items.push(
                  <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <div className={`group/bubble ${isMine ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white' : 'bg-white/5 text-white'} max-w-[78%] rounded-2xl ${radiusAdjust} px-4 py-2 border ${isMine ? 'border-emerald-400/40' : 'border-white/10'} shadow-sm`}> 
                      {m.attachment_url && (
                        <> 
                          {m.attachment_mime === 'application/pdf' ? (
                            <a href={m.signed_url || '#'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 mb-2">
                              <FileText className="h-4 w-4" /> View PDF
                            </a>
                          ) : (
                            <img src={m.signed_url || ''} alt="attachment" className="max-w-full rounded-lg border border-white/10 mb-2 cursor-zoom-in" onClick={() => setLightbox({ images: [m.signed_url || ''], index: 0 })} />
                          )}
                        </>
                      )}
                      {m.content && (
                        <div className={`prose prose-invert max-w-none ${isMine ? '[&_*]:text-white' : ''} prose-pre:bg-slate-900 prose-pre:border prose-pre:border-white/10`}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{m.content}</ReactMarkdown>
                        </div>
                      )}
                      <div className={`mt-1 text-[10px] flex items-center gap-1 ${isMine ? 'text-white/80' : 'text-white/60'} opacity-60 group-hover/bubble:opacity-100 transition-opacity`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {isMine && (
                          <span className="ml-1 inline-flex items-center" title={m.__receiptsCount > 0 ? 'Read' : 'Sent'}>
                            {m.__receiptsCount > 0 ? (
                              <CheckCheck className="h-3 w-3" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </div>
                      {onReport && (
                        <div className="opacity-0 hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className={`h-7 px-2 mt-1 ${isMine ? 'text-white/90 hover:text-white' : 'text-red-300'}`} onClick={async () => {
                            try {
                              await submitReport('message', m.id, 'other', 'Reported from chat window');
                              onReport?.(m.id);
                            } catch (e: any) {
                              toast({ title: 'Error', description: e.message || 'Failed to report', variant: 'destructive' });
                            }
                          }}>
                            <ShieldAlert className="h-4 w-4 mr-1" /> Report
                          </Button>
                        </div>
                      )}
                        </div>
                      </ContextMenuTrigger>
                      {isAdmin && (
                        <ContextMenuContent className="w-56">
                          <ContextMenuItem onClick={async () => {
                            const ok = window.confirm('Delete this message?');
                            if (!ok) return;
                            try {
                              await adminDeleteMessage(m.id);
                              setMessages((prev) => prev.filter((x) => x.id !== m.id));
                              toast({ title: 'Message deleted' });
                            } catch (e: any) {
                              toast({ title: 'Error', description: e.message || 'Failed to delete', variant: 'destructive' });
                            }
                          }} className="text-red-300 focus:text-red-200">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete message
                          </ContextMenuItem>
                          <ContextMenuItem onClick={async () => {
                            const ok = window.confirm('Delete all messages in this room?');
                            if (!ok) return;
                            try {
                              await adminDeleteAllMessagesInRoom(roomId);
                              setMessages([]);
                              toast({ title: 'All messages deleted' });
                            } catch (e: any) {
                              toast({ title: 'Error', description: e.message || 'Failed to delete all', variant: 'destructive' });
                            }
                          }} className="text-red-300 focus:text-red-200">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete all messages
                          </ContextMenuItem>
                        </ContextMenuContent>
                      )}
                    </ContextMenu>
                  </div>
                );
              }
              return items;
            })()}
            <div ref={endRef} />
          </div>
        </ScrollArea>
        {lightbox && (
          <ImageLightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} onPrev={() => setLightbox((s) => s ? { ...s, index: Math.max(0, s.index - 1) } : s)} onNext={() => setLightbox((s) => s ? { ...s, index: Math.min(s.images.length - 1, s.index + 1) } : s)} />
        )}
        {newNotice && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10">
            <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow" onClick={() => { scrollToBottom(); setNewNotice(false); }}>New messages</Button>
          </div>
        )}
        {uploadQueue.length > 0 && (
          <div className="px-3 pb-2 border-t border-white/10 bg-white/[0.02]">
            <div className="flex flex-wrap gap-2">
              {uploadQueue.map((u) => (
                <div key={u.name} className="flex items-center gap-2 px-2 py-1 rounded-full border border-white/10 bg-white/5 text-white/80 text-xs">
                  {u.status === 'uploading' && <Loader2 className="h-3 w-3 animate-spin text-emerald-300" />}
                  {u.status === 'done' && <Check className="h-3 w-3 text-emerald-300" />}
                  {u.status === 'error' && <XCircle className="h-3 w-3 text-red-300" />}
                  <span className="max-w-[180px] truncate">{u.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="p-3 border-t border-white/10 flex items-center gap-2 relative">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white/80 hover:text-white" title="Emoji">
                <span className="text-xl leading-none">😊</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-2 bg-slate-900 border border-white/10">
              {/* Simple emoji grid to keep bundle light (Apple/Android emoji render via OS fonts) */}
              <div className="grid grid-cols-8 gap-1">
                {['😀','😁','😂','🤣','😊','😍','😘','😎','🤩','😇','🙂','🤝','👍','👏','🙏','🔥','💯','✨','🎉','🌟','💎','🖼️','📎','📄','📝','🖊️','📌','💬','📷','🎥','🎧','🎁','🏆'].map((e) => (
                  <button key={e} className="h-8 w-8 rounded-md hover:bg-white/10" onClick={() => setText((t) => (t || '') + e)} aria-label={e}>
                    <span className="text-xl leading-none">{e}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Textarea value={text} onChange={(e) => {
            setText(e.target.value);
            try { localStorage.setItem(`chat:draft:${roomId}`, e.target.value); } catch {}
          }} placeholder="Write a message..." className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/50 resize-none" rows={1} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }} />
          <Button onClick={onSend} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400">
            <Send className="h-4 w-4 mr-1" /> Send
          </Button>
        </div>
        {/* Post-send security scan badge (fast bot-check) */}
        <div className="px-3 pb-3">
          <div className="inline-flex items-center gap-2 text-[11px] text-white/70 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
            <ScanLine className="h-3.5 w-3.5 text-emerald-300" /> Fast security scan active
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


